import { useState, useEffect, useRef, useCallback, memo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { List as VirtualList } from "react-window";
import {
  Mail, MessageSquare, Send, Search, Phone, PhoneCall, PhoneOff, Mic, MicOff,
  Volume2, Clock, Hash, Delete, Plus, Sparkles, Check, X, ExternalLink,
  Target, DollarSign, Loader2, Bot, ChevronDown, FileText, CalendarCheck, AlertTriangle,
  ChevronUp, ArrowRight, Zap, Edit2
} from "lucide-react";
import ComposeModal from "@/components/ComposeModal";
import FollowUpTaskModal from "@/components/FollowUpTaskModal";
import CreateTaskModal from "@/components/CreateTaskModal";
import TaskContextSummary from "@/components/TaskContextSummary";
import { sendRCSms, makeRCCall, linkRCActivityToLead } from "@/lib/rcIntegrationApi";
import { parseLocalDate, fmtLocalDate, isToday, isTomorrow } from "@/lib/dateLocal";
import { TaskDueText, TaskTimeInput } from "@/components/TaskDueTime";
import { isTaskOverdue, compareTaskDue, buildDueTimeFields, taskDueEditFields } from "@/lib/taskDue";
import RCSoftphone from "@/components/rc/RCSoftphone";
import RCWebPhone from "@/components/rc/RCWebPhone";
import VoicemailPanel from "@/components/rc/VoicemailPanel";
import MatchLeadPicker from "@/components/rc/MatchLeadPicker";

const DIALER_KEYS = [
  { num: "1", letters: "" }, { num: "2", letters: "ABC" }, { num: "3", letters: "DEF" },
  { num: "4", letters: "GHI" }, { num: "5", letters: "JKL" }, { num: "6", letters: "MNO" },
  { num: "7", letters: "PQRS" }, { num: "8", letters: "TUV" }, { num: "9", letters: "WXYZ" },
  { num: "*", letters: "" }, { num: "0", letters: "+" }, { num: "#", letters: "" },
];

/* ─── Signal pill component ─── */
function SignalPill({ text, color }) {
  const bg = color === "var(--green)" ? "var(--green-light)" : color === "var(--red)" ? "var(--red-light)" : color === "var(--amber)" ? "var(--amber-light)" : color === "var(--blue)" ? "var(--blue-light)" : color === "var(--purple)" ? "#f3f0ff" : "var(--gray-light)";
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px", borderRadius: 10, background: bg, color, fontSize: 10, fontWeight: 500 }}>{text}</span>;
}

/* ─── RingCentral Dialer (compact) ─── */
function DialerPanel({ prefillNumber }) {
  const { token } = useAuth();
  const [number, setNumber] = useState("");
  const [calling, setCalling] = useState(false);
  const [timer, setTimer] = useState(0);
  const [muted, setMuted] = useState(false);
  const [tab, setTab] = useState("phone");
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  // Orphan-row match flow — holds the activity row currently being matched.
  const [pickerRow, setPickerRow] = useState(null);

  useEffect(() => { if (prefillNumber) setNumber(prefillNumber.replace(/[^0-9+]/g, "")); }, [prefillNumber]);

  // CRM Activity tab: read CRM-attributed rc_call / rc_sms entries from the
  // activity_log (enriched with lead_id, delivery_status, error_code), NOT the
  // provider's raw call-log — that lives inside the RC embed to avoid duplicate
  // telephony surfaces.
  useEffect(() => {
    if (tab !== "activity" || !token) return;
    let cancelled = false;
    const load = async () => {
      setActivityLoading(true);
      try {
        const r = await axios.get(`${API}/integrations/rc/activity?limit=25`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setActivity(Array.isArray(r.data) ? r.data.filter(a => a.type === "rc_call_make" || a.type === "rc_sms_send") : []);
      } catch { /* silent during polling */ }
      if (!cancelled) setActivityLoading(false);
    };
    load();
    const i = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(i); };
  }, [tab, token]);

  const press = (k) => { if (!calling) setNumber(p => p + k); };
  const startCall = async () => { if (!number) return; setCalling(true); setTimer(0); try { await makeRCCall({ to: number }); } catch {} };
  const endCall = () => { setCalling(false); setTimer(0); toast.info("Call ended"); };

  return (
    <div style={{ borderTop: "0.5px solid var(--border)" }} data-testid="ringcentral-dialer">
      <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 6, borderBottom: "0.5px solid var(--border)" }}>
        <Phone style={{ width: 12, height: 12, color: "var(--blue)" }} />
        <span style={{ fontSize: 12, fontWeight: 650 }}>RingCentral</span>
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 3 }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--green)" }} />
          <span style={{ fontSize: 9, color: "var(--green)", fontWeight: 500 }}>Connected</span>
        </span>
      </div>
      <div style={{ display: "flex", borderBottom: "0.5px solid var(--border)" }}>
        {[{ id: "phone", label: "Phone" }, { id: "activity", label: "CRM Activity" }, { id: "voicemail", label: "Voicemail" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "6px 0", fontSize: 10, fontWeight: tab === t.id ? 600 : 400, color: tab === t.id ? "var(--blue)" : "var(--text-tertiary)", background: "none", border: "none", borderBottom: tab === t.id ? "2px solid var(--blue)" : "2px solid transparent", cursor: "pointer" }} data-testid={`dialer-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>
      {tab === "phone" && (
        <div style={{ padding: 8 }}>
          <RCWebPhone prefillNumber={prefillNumber} />
          <details style={{ marginTop: 10, fontSize: 10, color: "var(--text-tertiary)" }}>
            <summary style={{ cursor: "pointer", padding: "4px 6px" }} data-testid="legacy-softphone-toggle">
              Legacy iframe softphone (RC Embeddable)
            </summary>
            <div style={{ marginTop: 6 }}>
              <RCSoftphone prefillNumber={prefillNumber} />
            </div>
          </details>
          <div data-testid="phone-helper-note" style={{ fontSize: 10, color: "var(--text-tertiary)", padding: "8px 4px 0", textAlign: "center", lineHeight: 1.45 }}>
            Real WebRTC softphone — calls connect in your browser tab with audio. The legacy iframe widget remains as a fallback.
          </div>
        </div>
      )}
      {tab === "activity" && (
        <div style={{ maxHeight: 260, overflowY: "auto" }} data-testid="crm-activity-list">
          {activityLoading && activity.length === 0 ? (
            <div data-testid="crm-activity-loading" style={{ padding: "28px 12px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>
              Loading CRM activity…
            </div>
          ) : activity.length === 0 ? (
            <div data-testid="crm-activity-empty" style={{ padding: "28px 12px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 11 }}>
              No CRM-attributed call activity yet.
            </div>
          ) : activity.map((a, i) => {
            const isCall = a.type === "rc_call_make";
            const failed = a.delivery_status === "failed" || a.result === "failed";
            const providerReason = a.details?.provider_reason_description || a.details?.provider_reason;
            const when = a.updated_at || a.created_at;
            const whenLabel = when ? new Date(when).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
            const nameOrPhone = a.details?.lead_name || a.phone || "Unknown";
            const autoMatched = a.details?.auto_matched === true;
            const ambiguous = !a.lead_id && a.details?.auto_match_ambiguous === true;
            // Orphan detection — row has no lead attribution yet AND a usable phone.
            // Auto-matched rows are NOT orphans by definition (lead_id is set server-side).
            const isOrphan = !a.lead_id && !!a.phone;
            return (
              <div key={a.id || i}
                   data-testid={`crm-activity-row-${i}`}
                   onClick={() => { if (a.lead_id) window.location.assign(`/lead-focus/${a.lead_id}`); }}
                   style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px", borderBottom: "0.5px solid var(--border)", cursor: a.lead_id ? "pointer" : "default" }}
                   onMouseOver={e => { if (a.lead_id) e.currentTarget.style.background = "#fafaf9"; }}
                   onMouseOut={e => e.currentTarget.style.background = ""}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: failed ? "var(--red-light)" : isCall ? "var(--green-light)" : "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {failed ? <PhoneOff style={{ width: 11, height: 11, color: "var(--red)" }} /> : isCall ? <PhoneCall style={{ width: 11, height: 11, color: "var(--green)" }} /> : <MessageSquare style={{ width: 11, height: 11, color: "var(--blue)" }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: failed ? "var(--red)" : "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{nameOrPhone}</span>
                    {autoMatched && (
                      <span
                        data-testid={`auto-matched-badge-${a.id}`}
                        style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 8, background: "#E0F2FE", color: "#0369A1", whiteSpace: "nowrap" }}
                        title="Automatically linked to this lead by exact phone match"
                      >
                        Auto-matched
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>
                    {isCall ? "Call" : "SMS"} · {a.delivery_status || a.result || "—"}
                    {failed && providerReason ? ` · ${providerReason}` : ""}
                    {failed && a.error_code ? ` (${a.error_code})` : ""}
                  </div>
                  {ambiguous && (
                    <div
                      data-testid={`ambiguous-hint-${a.id}`}
                      style={{ fontSize: 9, color: "#b07400", marginTop: 2 }}
                    >
                      Multiple matches found — pick one below.
                    </div>
                  )}
                  {isOrphan && (
                    <button
                      type="button"
                      data-testid={`match-lead-chip-${a.id}`}
                      onClick={(e) => { e.stopPropagation(); setPickerRow(a); }}
                      style={{
                        marginTop: 4, padding: "2px 8px", fontSize: 9, fontWeight: 600,
                        color: "var(--blue)", background: "var(--blue-light)",
                        border: "none", borderRadius: 10, cursor: "pointer",
                      }}
                    >
                      Match to Lead
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 9, color: "var(--text-tertiary)", textAlign: "right", flexShrink: 0 }}>{whenLabel}</div>
              </div>
            );
          })}
        </div>
      )}
      {pickerRow && (
        <MatchLeadPicker
          activityPhone={pickerRow.phone}
          onCancel={() => setPickerRow(null)}
          onPick={async (lead) => {
            const row = pickerRow;
            // Optimistic update: hide chip immediately by stamping lead_id locally.
            const lead_name = lead.contact_name || lead.title || lead.company || "";
            setActivity((prev) => prev.map((r) => r.id === row.id ? { ...r, lead_id: lead.id, details: { ...(r.details || {}), lead_name } } : r));
            setPickerRow(null);
            try {
              await linkRCActivityToLead(row.id, lead.id);
            } catch {
              // Rollback: unset lead_id on failure.
              setActivity((prev) => prev.map((r) => r.id === row.id ? { ...r, lead_id: null } : r));
            }
          }}
        />
      )}
      {tab === "voicemail" && (
        <VoicemailPanel days={7} />
      )}
    </div>
  );
}

/* ─── Task constants ─── */
const TASK_PRIORITIES = [
  { id: "urgent", label: "Urgent", color: "#ff3b30", bg: "#fff1f0" },
  { id: "high", label: "High", color: "#ff9500", bg: "#fff8ee" },
  { id: "medium", label: "Medium", color: "#0071e3", bg: "#eef6ff" },
  { id: "low", label: "Low", color: "#8e8e93", bg: "#f5f5f7" },
];
const TASK_CATEGORIES = ["follow_up", "call", "email", "demo", "review", "other"];
const TASK_CAT_LABELS = { follow_up: "Follow-up", call: "Call", email: "Email", demo: "Demo", demo_prep: "Demo prep", review: "Review", other: "Other" };

/* Category derivation for a task — matches the inline categoryKey used
 * by the filter pipeline. Kept module-level so the virtualized TaskRow
 * doesn't re-declare it per render. */
function _catFor(t) {
  if (t?.category) return t.category;
  const s = t?.source;
  if (s && typeof s === "object" && s.kind) return s.kind;
  return "other";
}

/* ─── Virtualized Task Row (Iter 263, Jul 2026) ─────────────────────
 * Wrapped in memo so react-window can bail out of re-renders when the
 * task at a given index is unchanged. Receives all callbacks + shared
 * state via `rowProps` (react-window v2 passes it through unchanged).
 */
const TaskRow = memo(function TaskRow({ index, style, tasks, expandedTaskAction,
  setExpandedTaskAction, taskActionNote, setTaskActionNote, toggleTaskComplete,
  openEditTask, deleteTask, navigate, token }) {
  const task = tasks[index];
  if (!task) return <div style={style} />;
  const pri = TASK_PRIORITIES.find(p => p.id === task.priority) || TASK_PRIORITIES[2];
  const isOverdue = isTaskOverdue(task);
  const isUrgent = isOverdue && (task.priority === "high" || task.priority === "urgent");
  const cat = _catFor(task);
  const catLabel = TASK_CAT_LABELS[cat] || cat.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Other";
  const isDemo = cat === "demo" || cat === "demo_prep";
  const isExpanded = expandedTaskAction === task.id;
  return (
    <div style={{ ...style, paddingBottom: 4 }}>
      <div style={{ borderRadius: 8, overflow: "hidden" }}>
        <div className="card" style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, opacity: task.status === "completed" ? 0.6 : 1, borderLeft: `3px solid ${pri.color}`, background: isUrgent ? "#FFF8F0" : "#fff", borderRadius: isExpanded ? "8px 8px 0 0" : 8 }} data-testid={`comm-task-${task.id}`}>
          <button onClick={() => toggleTaskComplete(task)} style={{ width: 22, height: 22, borderRadius: 6, border: task.status === "completed" ? "none" : `1.5px solid ${pri.color}`, background: task.status === "completed" ? "#34c759" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }} data-testid={`toggle-task-${task.id}`}>
            {task.status === "completed" && <Check style={{ width: 14, height: 14, color: "#fff" }} />}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Sparkles style={{ width: 12, height: 12, color: "#3C3489", flexShrink: 0 }} title="AI-tracked task" />
              <span style={{ fontSize: 13, fontWeight: 600, textDecoration: task.status === "completed" ? "line-through" : "none", color: "var(--text-primary)" }}>
                {task.title}
              </span>
              {isUrgent && <span style={{ marginLeft: 4, background: "#FCEBEB", color: "#A32D2D", borderRadius: 10, fontSize: 10, padding: "2px 6px", fontWeight: 500 }}>Act now</span>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
              <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 6px", borderRadius: 4, background: pri.bg, color: pri.color }}>{pri.label}</span>
              <span style={{ fontSize: 9, fontWeight: 500, padding: "1px 6px", borderRadius: 4, background: "#EEEDFE", color: "#3C3489" }}>{catLabel}</span>
              {task.lead_name && <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{task.lead_name}</span>}
              {task.assigned_to_name && <span style={{ fontSize: 10, color: "var(--blue)" }}>{task.assigned_to_name}</span>}
              {isDemo && (
                <button onClick={() => navigate("/demo-bookings")} style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, color: "var(--purple)", background: "#f3f0ff", border: "none", borderRadius: 4, padding: "1px 6px", cursor: "pointer", fontFamily: "var(--font)" }} data-testid={`task-demo-link-${task.id}`}>
                  <CalendarCheck style={{ width: 9, height: 9 }} /> Demo Bookings
                </button>
              )}
            </div>
            <TaskContextSummary task={task} compact />
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            {task.due_date && <div style={{ fontSize: 10, color: isOverdue ? "#ff3b30" : "var(--text-tertiary)", fontWeight: isOverdue ? 600 : 400 }}>{isOverdue && "Overdue · "}<TaskDueText task={task} overdue={isOverdue} testid={`comm-task-due-text-${task.id}`} /></div>}
            {task.completed_at && <div style={{ fontSize: 9, color: "#34c759" }}>Done {fmtLocalDate(task.completed_at)}</div>}
          </div>
          {task.status !== "completed" && (
            <button onClick={() => { setExpandedTaskAction(isExpanded ? null : task.id); setTaskActionNote(""); }}
              style={{ border: "0.5px solid #2563eb", color: "#2563eb", background: isExpanded ? "#E6F1FB" : "#fff", borderRadius: 8, padding: "4px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}
              data-testid={`take-action-${task.id}`}>
              Take action <ArrowRight style={{ width: 10, height: 10 }} />
            </button>
          )}
          <button onClick={() => openEditTask(task)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }} title="Edit task" data-testid={`edit-task-${task.id}`}>
            <Edit2 style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
          </button>
          <button onClick={() => deleteTask(task.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }} data-testid={`delete-task-${task.id}`}>
            <X style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
          </button>
        </div>

        {/* Inline Action Panel */}
        {isExpanded && task.status !== "completed" && (
          <div style={{ background: "#f9f9f9", border: "0.5px solid #eee", borderRadius: "0 0 8px 8px", padding: "12px 16px", borderTop: "none" }} data-testid={`action-panel-${task.id}`}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {(cat === "call" || cat === "follow_up" || cat === "call_back") && (<>
                <button
                  data-testid={`task-call-btn-${task.id}`}
                  onClick={async () => {
                    // Iter 158 (Josh): auto-hydrate phone from lead if missing.
                    const qs = new URLSearchParams();
                    qs.set("task_id", task.id);
                    if (task.lead_id) qs.set("lead_id", task.lead_id);
                    let phone = task.phone;
                    if (!phone && task.lead_id) {
                      try {
                        const r = await axios.get(`${API}/leads/${task.lead_id}`, { headers: { Authorization: `Bearer ${token}` }, timeout: 6000 });
                        const ld = r.data || {};
                        phone = (ld.contacts?.[0]?.phone) || ld.phone || (ld.locations?.[0]?.phone) || "";
                      } catch (e) {
                        console.warn("[task-call] lead lookup failed", e?.message);
                      }
                    }
                    if (!phone) {
                      toast.error("No phone number on file for this lead. Open the lead and add one first.");
                      return;
                    }
                    qs.set("phone", phone);
                    const label = task.lead_name || task.title || "";
                    if (label) qs.set("name", label);
                    navigate(`/dialer?${qs.toString()}`);
                  }}
                  style={{ border: "0.5px solid #FAC775", color: "#854F0B", background: "#fff", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }}>
                  <Phone style={{ width: 11, height: 11 }} /> Call now
                </button>
                <button onClick={() => task.lead_id ? navigate(`/lead-focus/${task.lead_id}`) : toast.info("No linked lead")} style={{ fontSize: 12, color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>Log call instead</button>
              </>)}
              {(cat === "email" || cat === "review") && (<>
                <button onClick={() => task.lead_id ? navigate(`/lead-focus/${task.lead_id}`) : toast.info("No linked lead")} className="btn btn-primary" style={{ fontSize: 11, padding: "5px 12px" }}>
                  Open lead <ArrowRight style={{ width: 10, height: 10 }} />
                </button>
                <button onClick={() => { toggleTaskComplete(task); }} style={{ border: "0.5px solid #9FE1CB", color: "#3B6D11", background: "#EAF3DE", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)" }}>Mark sent</button>
              </>)}
              {(cat === "demo" || cat === "demo_prep") && (<>
                <button onClick={() => navigate("/demo-bookings")} style={{ border: "0.5px solid #9FE1CB", color: "#0F6E56", background: "#fff", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }}>
                  <CalendarCheck style={{ width: 11, height: 11 }} /> Go to Demo Bookings
                </button>
                <button onClick={() => { toggleTaskComplete(task); }} style={{ border: "0.5px solid #9FE1CB", color: "#3B6D11", background: "#EAF3DE", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)" }}>Mark prepped</button>
              </>)}
              {(cat === "other" || (!["call","follow_up","call_back","email","review","demo","demo_prep"].includes(cat))) && (<>
                <button onClick={() => task.lead_id ? navigate(`/lead-focus/${task.lead_id}`) : toast.info("No linked lead")} style={{ border: "0.5px solid #2563eb", color: "#2563eb", background: "#fff", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }}>
                  <Mail style={{ width: 11, height: 11 }} /> Email lead
                </button>
                <button onClick={() => { toggleTaskComplete(task); }} style={{ border: "0.5px solid #9FE1CB", color: "#3B6D11", background: "#EAF3DE", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }}>
                  <Check style={{ width: 11, height: 11 }} /> Mark complete
                </button>
              </>)}
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: "#888", display: "block", marginBottom: 4 }}>Quick note (saved to lead activity):</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input value={taskActionNote} onChange={e => setTaskActionNote(e.target.value)} placeholder="Add a note..."
                  style={{ flex: 1, padding: "6px 10px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "var(--font)" }} />
                <button onClick={async () => {
                    const body = taskActionNote.trim();
                    if (!body) return;
                    if (task.lead_id) {
                      try {
                        await axios.post(`${API}/leads/${task.lead_id}/notes`,
                          { body: `[Task: ${task.title || "—"}] ${body}` },
                          { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
                        );
                        toast.success("Note saved to lead activity");
                        setTaskActionNote("");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Couldn't save note — please retry.");
                      }
                    } else {
                      toast.error("No linked lead — can't save note");
                    }
                  }}
                  style={{ padding: "6px 12px", borderRadius: 6, border: "0.5px solid #ddd", background: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)" }}>Save note</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

/* ─── Main Communications Page ─── */
export default function Communications() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter 323 (Feb 2026) — Communications conversations were previously
  // a hard-coded empty array. The list is now fetched from
  // `GET /api/communications/email/conversations`, which enforces
  // strict per-user isolation server-side (a rep only sees email
  // threads that involve their own CRM account email / user id).
  // Refresh cadence: on mount, on window focus, and after a
  // successful compose. No polling loop to avoid Mongo pressure.
  const [conversations, setConversations] = useState([]);
  const [convsLoading, setConvsLoading] = useState(false);
  const [internalHidden, setInternalHidden] = useState(0);
  // Iter 324 (Feb 2026) — "Hide internal emails" toggle. Persists per-
  // browser via localStorage so a rep who prefers external-only view
  // doesn't have to re-check the box every visit.
  const [hideInternal, setHideInternal] = useState(() => {
    try { return localStorage.getItem("eh.comms.hideInternal") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("eh.comms.hideInternal", hideInternal ? "1" : "0"); }
    catch { /* ignore */ }
  }, [hideInternal]);
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      setConvsLoading(true);
      const r = await axios.get(`${API}/communications/email/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hide_internal: hideInternal ? "true" : "false" },
        timeout: 20000,
      });
      setConversations(Array.isArray(r?.data?.items) ? r.data.items : []);
      setInternalHidden(Number(r?.data?.internal_hidden || 0));
    } catch (e) {
      console.warn("[communications] fetchConversations failed", e?.message);
    } finally {
      setConvsLoading(false);
    }
  }, [token, hideInternal]);
  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => {
    const onFocus = () => fetchConversations();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchConversations]);
  const [activeConv, setActiveConv] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState("email");
  const [analyzing, setAnalyzing] = useState(false);
  const [quoteContext, setQuoteContext] = useState(null);
  const [commTab, setCommTab] = useState(searchParams.get("tab") === "tasks" ? "tasks" : "conversations");
  const [tasks, setTasks] = useState([]);
  const _userRole = (user?.role || "").toLowerCase();
  const _isManager = ["super_admin", "crm_admin", "sales_manager", "marketing_manager"].includes(_userRole);
  const [taskScope, setTaskScope] = useState(_isManager ? "team" : "mine");
  // Iter 159 (Josh — issue #616ff0f5 / Ioannis): persist task filters in URL
  // so "Today" + status filters survive page navigation. Reads on initial
  // mount; writes back on every filter change via a controlled effect.
  const [taskFilter, setTaskFilter] = useState({
    priority: searchParams.get("tf_priority") || "all",
    due: searchParams.get("tf_due") || "all",
    type: searchParams.get("tf_type") || "all",
    source: searchParams.get("tf_source") || "all",
  });
  // Iter 210.3 (Feb 2026 · April "completed tasks still showing" fix) —
  // Default the status filter to "pending" so reps see only actionable
  // work when opening the Tasks tab. Completed rows previously stayed
  // visible with just line-through + 60% opacity, which reps read as
  // "the task didn't complete". They can still flip to "All" or
  // "Completed" for history. URL params override so bookmarks
  // continue to work exactly as before.
  const [taskStatusFilter, setTaskStatusFilter] = useState(searchParams.get("tf_status") || "pending");
  const [taskSearch, setTaskSearch] = useState(searchParams.get("tf_q") || "");
  const [showCreateTask, setShowCreateTask] = useState(false);
  // ── FollowUpTaskModal wiring (opens when there is a lead context in the URL) ──
  const [followUpLead, setFollowUpLead] = useState(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const urlLeadId = searchParams.get("lead_id") || "";
  const urlLeadName = searchParams.get("lead_name") || "";
  const hasLeadContext = Boolean(urlLeadId || urlLeadName);

  const openNewTask = async () => {
    if (!hasLeadContext) {
      setShowCreateTask(true);
      return;
    }
    if (urlLeadId) {
      try {
        const res = await axios.get(`${API}/leads/${urlLeadId}`, { headers: { Authorization: `Bearer ${token}` } });
        setFollowUpLead(res.data || { id: urlLeadId, clinic: urlLeadName || "Lead" });
      } catch {
        setFollowUpLead({ id: urlLeadId, clinic: urlLeadName || "Lead" });
      }
    } else {
      setFollowUpLead({ clinic: urlLeadName });
    }
    setShowFollowUp(true);
  };
  const [expandedTaskAction, setExpandedTaskAction] = useState(null);
  const [taskActionNote, setTaskActionNote] = useState("");
  const [aiPanelOpen, setAiPanelOpen] = useState(true);
  const [aiSorted, setAiSorted] = useState(false);
  // Iter 262 (Jul 2026) — server-side task counters. The header used to
  // count pending/overdue/today client-side from the paginated payload,
  // which produced "Due Today (0)" whenever today's tasks lived beyond
  // the fetch cap. Now sourced from GET /tasks/counts (exact numbers).
  const [taskCounts, setTaskCounts] = useState({
    total: 0, pending: 0, completed: 0, overdue: 0, due_today: 0, due_tomorrow: 0,
  });
  const threadEndRef = useRef(null);

  useEffect(() => {
    // Iter 262 — pass server-side `status` filter so completed tasks
    // don't consume the 2,000-row fetch budget when the rep is viewing
    // pending work (the default).
    const params = { scope: taskScope };
    if (taskStatusFilter === "pending") params.status = "pending";
    else if (taskStatusFilter === "completed") params.status = "completed";
    axios.get(`${API}/tasks`, { params, headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTasks(r.data || []))
      .catch(() => {});
    axios.get(`${API}/tasks/counts`, { params: { scope: taskScope }, headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTaskCounts(r.data || {}))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- token read at call-time
  }, [taskScope, taskStatusFilter]);

  // Sync tab from URL params
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "tasks" && commTab !== "tasks") setCommTab("tasks");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- commTab intentionally compared without retriggering the effect
  }, [searchParams]);

  // Iter 159 — write task filters back to the URL only when on the Tasks
  // tab so the bookmark / back-button restores the rep's filter state.
  useEffect(() => {
    if (commTab !== "tasks") return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", "tasks");
    const setOrDelete = (k, v, dflt) => { if (v && v !== dflt) next.set(k, v); else next.delete(k); };
    setOrDelete("tf_priority", taskFilter.priority, "all");
    setOrDelete("tf_due", taskFilter.due, "all");
    setOrDelete("tf_type", taskFilter.type, "all");
    setOrDelete("tf_source", taskFilter.source, "all");
    setOrDelete("tf_status", taskStatusFilter, "pending");
    setOrDelete("tf_q", taskSearch, "");
    const a = next.toString(), b = searchParams.toString();
    if (a !== b) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearchParams ref-stable
  }, [commTab, taskFilter, taskStatusFilter, taskSearch]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setCommTab(tab);
    if (tab === "tasks") setSearchParams({ tab: "tasks" });
    else setSearchParams({});
  };

  const fetchTasks = async () => {
    try {
      const params = { scope: taskScope };
      if (taskStatusFilter === "pending") params.status = "pending";
      else if (taskStatusFilter === "completed") params.status = "completed";
      const res = await axios.get(`${API}/tasks`, { params, headers: { Authorization: `Bearer ${token}` } });
      setTasks(res.data || []);
    } catch { /* silent */ }
    try {
      const cRes = await axios.get(`${API}/tasks/counts`, { params: { scope: taskScope }, headers: { Authorization: `Bearer ${token}` } });
      setTaskCounts(cRes.data || {});
    } catch { /* silent */ }
  };

  // Iter 158 (Josh) — issue #792c36da: when a rep creates a task via
  // the call disposition modal (post-call), refetch the tasks list so
  // it appears in the Tasks tab immediately without manual reload.
  useEffect(() => {
    const onRefresh = () => fetchTasks();
    window.addEventListener("tasks:refreshed", onRefresh);
    return () => window.removeEventListener("tasks:refreshed", onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchTasks reads fresh state
  }, []);

  const toggleTaskComplete = async (task) => {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus, completed_at: newStatus === "completed" ? new Date().toISOString() : null } : t));
      toast.success(newStatus === "completed" ? "Task completed" : "Task reopened");
      // Iter 159 (#af74bef9 / Priya) — broadcast so Dashboard refetches and
      // the "Needs your attention" widget drops the now-completed task.
      try { window.dispatchEvent(new CustomEvent("tasks:refreshed", { detail: { source: "comms_tasks" } })); } catch { /* noop */ }
    } catch (e) {
      // Iter 158 (Josh) — issue reports #5/#40/#48 ("mark complete does
      // nothing"). Surface the real backend error (403/404 etc) instead
      // of the generic "Failed to update" — and refetch on failure so
      // the UI matches server state.
      const detail = e?.response?.data?.detail || e?.message || "Failed to update";
      toast.error(`Couldn't update task: ${detail}`);
      fetchTasks();
    }
  };

  const deleteTask = async (id) => {
    // Iter 158 (Josh) — issue report #1: confirm before delete.
    // Stephanie hit the X expecting "dismiss" → task was permanently gone.
    const t = tasks.find(x => x.id === id);
    const label = t?.title ? `"${t.title}"` : "this task";
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/tasks/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(prev => prev.filter(t => t.id !== id));
      toast.success("Task deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  // Iter 159 (Josh — #48003f98 / Ioannis): edit a task in place
  // (title / notes / due-date / priority / category).
  const [editTaskState, setEditTaskState] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({ title: "", description: "", priority: "medium", category: "follow_up", due_date: "", due_time: "" });
  const openEditTask = (task) => {
    setEditTaskState(task);
    setEditTaskForm({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "medium",
      category: task.category || "follow_up",
      ...taskDueEditFields(task),
    });
  };
  const saveEditTask = async () => {
    if (!editTaskState) return;
    if (!editTaskForm.title.trim()) { toast.error("Title required"); return; }
    try {
      await axios.put(`${API}/tasks/${editTaskState.id}`, { ...editTaskForm, due_time: undefined, ...buildDueTimeFields(editTaskForm.due_date, editTaskForm.due_time) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Task updated");
      setEditTaskState(null);
      fetchTasks();
      try { window.dispatchEvent(new CustomEvent("tasks:refreshed", { detail: { source: "comms_edit" } })); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  // Check for quote context from sessionStorage (quote-to-comms flow)
  useEffect(() => {
    const ctx = sessionStorage.getItem("quote_context");
    if (ctx) {
      try {
        const parsed = JSON.parse(ctx);
        setQuoteContext(parsed);
        setComposeMode(parsed.mode || "email");
        setComposeOpen(true);
        sessionStorage.removeItem("quote_context");
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (activeConv && threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [activeConv]);

  const filtered = conversations.filter(c => {
    if (search && !(c.contactName + c.company + c.lastMessage).toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === "unread") return c.unread;
    if (filter === "needs_action") return c.needsAction;
    if (filter === "awaiting") return c.aiSignal?.text?.toLowerCase().includes("awaiting");
    return true;
  });

  const stats = [
    { icon: Mail, label: "Emails", value: conversations.reduce((s,c) => s + c.messages.filter(m => m.channel === "email").length, 0) },
    { icon: MessageSquare, label: "SMS", value: conversations.reduce((s,c) => s + c.messages.filter(m => m.channel === "sms").length, 0) },
    { icon: Phone, label: "Calls today", value: 0 },
    { icon: Send, label: "Sent", value: conversations.reduce((s,c) => s + c.messages.filter(m => m.direction === "outbound").length, 0) },
  ];

  const openCompose = (mode) => { setComposeMode(mode || "email"); setComposeOpen(true); };

  const analysisBar = activeConv?.nextAction ? (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "var(--blue-light)", borderRadius: 0, fontSize: 11 }}>
      <Sparkles style={{ width: 11, height: 11, color: "var(--blue)", flexShrink: 0 }} />
      <span style={{ color: "var(--blue)", flex: 1 }}>Claude: {activeConv.aiSignal?.text} — {activeConv.nextAction}</span>
      <button className="btn" style={{ fontSize: 9, padding: "2px 8px" }}>View detail</button>
      <button className="btn" style={{ fontSize: 9, padding: "2px 8px" }} onClick={() => toast.success("Marked done")}>Mark done</button>
    </div>
  ) : null;

  return (
    <div data-testid="communications-page" data-mode="real" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 52px)" }}>
      {/* Top bar with stats */}
      <div style={{ padding: "10px 20px 0", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div><h1 className="page-title" style={{ marginBottom: 0 }}>Communications</h1></div>
          <div style={{ display: "flex", gap: 4 }}>
            <span className="tag tag-green" style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px" }}><span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--green)" }} /> Microsoft 365</span>
            <span className="tag tag-green" style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px" }}><span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--green)" }} /> RingCentral</span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 8 }}>
          {stats.map((s, i) => (
            <div key={i} className="stat-card" style={{ padding: "8px 12px" }}>
              <s.icon style={{ width: 14, height: 14, color: "var(--blue)", marginBottom: 3 }} />
              <div className="stat-card-value" style={{ fontSize: 16 }}>{s.value}</div>
              <div className="stat-card-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Page Tab Toggle */}
      <div style={{ padding: "0 20px", display: "flex", gap: 0, borderBottom: "0.5px solid var(--border)", flexShrink: 0 }}>
        {[{ id: "conversations", label: "Conversations" }, { id: "tasks", label: "Tasks & Follow-ups" }].map(t => (
          <button key={t.id} onClick={() => handleTabChange(t.id)} data-testid={`comm-tab-${t.id}`}
            style={{ padding: "10px 20px", fontSize: 12, fontWeight: 600, cursor: "pointer", background: "none", border: "none", fontFamily: "var(--font)", color: commTab === t.id ? "var(--blue)" : "var(--text-tertiary)", borderBottom: commTab === t.id ? "2px solid var(--blue)" : "2px solid transparent" }}>
            {t.label} {t.id === "tasks" && taskCounts.pending > 0 && <span className="tag tag-blue" style={{ marginLeft: 4, fontSize: 9 }}>{taskCounts.pending}</span>}
          </button>
        ))}
      </div>

      {commTab === "tasks" && (() => {
        const pendingTasks = tasks.filter(t => t.status !== "completed");
        const overdueTasks = pendingTasks.filter(t => isTaskOverdue(t));
        const dueTodayTasks = pendingTasks.filter(t => isToday(t.due_date));

        // Iter 184.1 (Feb 2026 — urgent: Ioannis #ba6e1ff8, Tatiana
        // #db4e4a71, super-admin escalation) — tasks were storing
        // `source` as a structured object ({type, kind, source_id,
        // trigger}) but the filter compared the whole object against
        // the string from the chip. Every chip selection produced an
        // empty list because `{…} !== "manual"` is always true. Same
        // problem for `category`: AI-extracted call tasks never set
        // `category`, so filtering by `tf_type=call` wiped them all
        // out. We normalise both ends so old (string) and new (dict)
        // shapes both work.
        const sourceKey = (t) => {
          // Map the backend's heterogeneous source schema onto the chip
          // values shown in the UI: ["all", "call_log", "manual", "ai_agent"].
          //
          // Fix M (Iter 261, Jul 2026): AI-generated tasks flow in from
          // multiple sources (`meeting_insights`, `auto_demo_no_show`,
          // `dialer_postcall`, plus objects with `type: "ai_agent"`), but
          // previously only `call_summary` was mapped and everything else
          // fell through to "manual" — burying real manual tasks under a
          // pile of AI rows. We now split cleanly:
          //   • call-derived summaries → "call_log" chip
          //   • other AI-agent sources → "ai_agent" chip
          //   • everything else / no source → "manual" chip
          const AI_AGENT_SOURCES = new Set([
            "ai_agent",
            "meeting_insights",
            "auto_demo_no_show",
            "dialer_postcall",
          ]);
          const s = t?.source;
          if (!s) return "manual";
          if (typeof s === "string") {
            if (s === "call_summary" || s === "call_log") return "call_log";
            if (AI_AGENT_SOURCES.has(s)) return "ai_agent";
            if (s === "manual") return "manual";
            return s;
          }
          if (typeof s === "object" && s.type) {
            if (s.type === "call_summary" || s.type === "call_log") return "call_log";
            if (AI_AGENT_SOURCES.has(s.type)) return "ai_agent";
            return s.type;
          }
          return "manual";
        };
        const categoryKey = (t) => {
          if (t?.category) return t.category;
          const s = t?.source;
          if (s && typeof s === "object" && s.kind) return s.kind;
          return "other";
        };

        const filteredTasks = tasks.filter(t => {
          if (taskStatusFilter === "pending" && t.status === "completed") return false;
          if (taskStatusFilter === "completed" && t.status !== "completed") return false;
          if (taskFilter.priority !== "all" && t.priority !== taskFilter.priority) return false;
          if (taskFilter.type !== "all" && categoryKey(t) !== taskFilter.type) return false;
          if (taskFilter.due === "today" && !isToday(t.due_date)) return false;
          if (taskFilter.due === "tomorrow" && !isTomorrow(t.due_date)) return false;
          if (taskFilter.due === "overdue" && !isTaskOverdue(t)) return false;
          if (taskFilter.source !== "all" && sourceKey(t) !== taskFilter.source) return false;
          if (taskSearch) {
            const s = taskSearch.toLowerCase();
            if (!(t.title || "").toLowerCase().includes(s) && !(t.lead_name || "").toLowerCase().includes(s) && !(t.assigned_to_name || "").toLowerCase().includes(s)) return false;
          }
          return true;
        }).sort((a, b) => {
          const aUrgent = a.priority === "high" && isTaskOverdue(a);
          const bUrgent = b.priority === "high" && isTaskOverdue(b);
          if (aUrgent && !bUrgent) return -1;
          if (!aUrgent && bUrgent) return 1;
          return compareTaskDue(a, b);
        });

        return (
        <div style={{ flex: 1, overflow: "auto", padding: 20 }} data-task-counts={JSON.stringify({ pending: taskCounts.pending, overdue: taskCounts.overdue, today: taskCounts.due_today, tomorrow: taskCounts.due_tomorrow, completed: taskCounts.completed })}>
          {/* Stat Tiles — sourced from GET /tasks/counts (Iter 262) so numbers
              stay exact even when the visible list is paginated.
              Iter 210.3 (Feb 2026 · April "1095 tasks" clarity fix) —
              renamed the leftmost tile from "Total Tasks" to "All-Time
              Tasks" and added a hover tooltip so reps understand the
              number includes completed history, not just work-to-do. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }} data-testid="task-stat-tiles">
            {[
              { label: "All-Time Tasks", value: taskCounts.total, color: "#0071e3", bg: "#eef6ff", tip: `${taskCounts.total} tasks ever created (includes completed history). See "Pending" for actionable work.` },
              { label: "Pending", value: taskCounts.pending, color: "#ff9500", bg: "#fff8ee", tip: `${taskCounts.pending} tasks not yet completed. This is your live queue.` },
              { label: "Overdue", value: taskCounts.overdue, color: "#ff3b30", bg: "#fff1f0", tip: `${taskCounts.overdue} pending tasks past their due date.` },
              { label: "Due Today", value: taskCounts.due_today, color: "#34c759", bg: "#edfcf2", tip: `${taskCounts.due_today} pending tasks due today.` },
            ].map((s, i) => (
              <div
                key={i}
                className="card"
                title={s.tip}
                style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "help" }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.value}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* AI Intelligence Panel (1.4) */}
          <div style={{ background: "#0F2942", borderRadius: 10, padding: aiPanelOpen ? "14px 18px" : "10px 18px", marginBottom: 16, transition: "padding 0.2s" }} data-testid="ai-task-panel">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setAiPanelOpen(!aiPanelOpen)}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>Claude AI — Task Intelligence</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 10, color: "#8BA5C0" }}>Updated just now</span>
                {aiPanelOpen ? <ChevronUp style={{ width: 12, height: 12, color: "#8BA5C0" }} /> : <ChevronDown style={{ width: 12, height: 12, color: "#8BA5C0" }} />}
              </div>
            </div>
            {aiPanelOpen && (
              <>
                <div style={{ marginTop: 12 }}>
                  {taskCounts.overdue > 0 && (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff3b30", marginTop: 4, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>{taskCounts.overdue} overdue high-priority task{taskCounts.overdue !== 1 ? "s" : ""}{overdueTasks.length > 0 ? ` — ${overdueTasks.slice(0, 2).map(t => t.lead_name || t.title).join(", ")}${taskCounts.overdue > 2 ? ` +${taskCounts.overdue - 2} more` : ""}` : ""}</span>
                    </div>
                  )}
                  {taskCounts.due_today > 0 && (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff9500", marginTop: 4, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>{taskCounts.due_today} task{taskCounts.due_today !== 1 ? "s" : ""} due today linked to pipeline deals</span>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#34c759", marginTop: 4, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>{tasks.filter(t => t.category === "demo" || t.category === "demo_prep").length} upcoming demo prep items to review</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#0071e3", marginTop: 4, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>{pendingTasks.length > 0 ? `Most tasks cluster around follow-ups — consider batch outreach` : "No pending tasks — great job staying on top of things"}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button onClick={() => { setAiSorted(!aiSorted); toast.success(aiSorted ? "Default order restored" : "Tasks re-sorted by AI priority"); }}
                    style={{ background: "rgba(255,255,255,0.10)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.20)", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }} data-testid="ai-prioritize-btn">
                    <Sparkles style={{ width: 10, height: 10 }} /> {aiSorted ? "Reset order" : "Prioritize my tasks"}
                  </button>
                  <button onClick={() => toast.info("AI is drafting follow-up emails...")}
                    style={{ background: "rgba(255,255,255,0.10)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.20)", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }}>
                    <Sparkles style={{ width: 10, height: 10 }} /> Draft follow-up emails
                  </button>
                  <button onClick={() => toast.info("AI is grouping tasks for batch actions...")}
                    style={{ background: "rgba(255,255,255,0.10)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.20)", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }}>
                    <Sparkles style={{ width: 10, height: 10 }} /> Suggest batch actions
                  </button>
                </div>
              </>
            )}
          </div>

          {aiSorted && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <span style={{ background: "#EEEDFE", color: "#3C3489", borderRadius: 10, fontSize: 10, padding: "2px 8px", fontWeight: 500 }}>AI sorted</span>
              <button onClick={() => setAiSorted(false)} style={{ fontSize: 11, color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>Reset to default order</button>
            </div>
          )}

          {/* Status Filter Tabs + Search + New Task */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {[{ key: "all", label: "All" }, { key: "pending", label: "Pending" }, { key: "completed", label: "Completed" }].map(f => (
                <button key={f.key} onClick={() => setTaskStatusFilter(f.key)} data-testid={`task-status-${f.key}`}
                  style={{ padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: taskStatusFilter === f.key ? 600 : 400, background: taskStatusFilter === f.key ? "#1b365D" : "var(--gray-light)", color: taskStatusFilter === f.key ? "#fff" : "var(--text-tertiary)", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>{f.label}</button>
              ))}
              {_isManager && (
                <div style={{ display: "flex", gap: 0, marginLeft: 10, borderRadius: 6, overflow: "hidden", border: "0.5px solid var(--border)" }} data-testid="task-scope-toggle">
                  {[{ key: "team", label: "Team" }, { key: "mine", label: "Mine" }].map(s => (
                    <button key={s.key} onClick={() => setTaskScope(s.key)} data-testid={`task-scope-${s.key}`}
                      style={{ padding: "5px 12px", fontSize: 11, fontWeight: taskScope === s.key ? 600 : 400, background: taskScope === s.key ? "#0F2942" : "#fff", color: taskScope === s.key ? "#fff" : "var(--text-tertiary)", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>{s.label}</button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ position: "relative" }}>
                <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "var(--text-tertiary)" }} />
                <input value={taskSearch} onChange={e => setTaskSearch(e.target.value)} placeholder="Search tasks..." className="input" style={{ paddingLeft: 28, fontSize: 11, height: 30, width: 200 }} data-testid="task-search-input" />
              </div>
              <button onClick={openNewTask} className="btn btn-primary" style={{ fontSize: 11, padding: "6px 14px" }} data-testid="create-task-btn">
                <Plus style={{ width: 12, height: 12 }} /> New Task
              </button>
            </div>
          </div>

          {/* Advanced Filter Bar */}
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "10px 14px", background: "#fff", border: "0.5px solid #eee", borderRadius: 8, marginBottom: 12 }} data-testid="task-filter-bar">
            <span style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px", flexShrink: 0 }}>Filter:</span>
            <div style={{ display: "flex", gap: 3 }}>
              {["all", "high", "medium", "low"].map(p => {
                const cfg = { all: { bg: "#f5f5f5", color: "#888" }, high: { bg: "#FCEBEB", color: "#A32D2D", border: "#F7C1C1" }, medium: { bg: "#FAEEDA", color: "#854F0B", border: "#FAC775" }, low: { bg: "#EAF3DE", color: "#3B6D11", border: "#9FE1CB" } };
                const c = cfg[p];
                return <button key={p} onClick={() => setTaskFilter(f => ({ ...f, priority: p }))} style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, border: taskFilter.priority === p ? `0.5px solid ${c.border || "#888"}` : "0.5px solid transparent", background: taskFilter.priority === p ? c.bg : "#f5f5f5", color: taskFilter.priority === p ? c.color : "#888", cursor: "pointer", fontFamily: "var(--font)", textTransform: "capitalize" }}>{p}</button>;
              })}
            </div>
            <select value={taskFilter.due} onChange={e => setTaskFilter(f => ({ ...f, due: e.target.value }))} data-testid="task-filter-due" style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, border: "0.5px solid #ddd", background: "#fff", fontFamily: "var(--font)", color: "#888" }}>
              <option value="all">Due: Any time ({taskCounts.pending})</option>
              <option value="today">Today ({taskCounts.due_today})</option>
              <option value="tomorrow">Tomorrow ({taskCounts.due_tomorrow})</option>
              <option value="overdue" style={{ color: "#A32D2D" }}>Overdue ({taskCounts.overdue})</option>
            </select>
            <select value={taskFilter.type} onChange={e => setTaskFilter(f => ({ ...f, type: e.target.value }))} style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, border: "0.5px solid #ddd", background: "#fff", fontFamily: "var(--font)", color: "#888" }}>
              <option value="all">Type: All</option><option value="call">Call back</option><option value="email">Email</option><option value="demo">Demo</option><option value="follow_up">Follow-up</option><option value="review">Review</option>
            </select>
            <div style={{ display: "flex", gap: 3 }}>
              {["all", "call_log", "manual", "ai_agent"].map(s => (
                <button key={s} onClick={() => setTaskFilter(f => ({ ...f, source: s }))} style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, border: taskFilter.source === s ? "0.5px solid #CECBF6" : "0.5px solid #ddd", background: taskFilter.source === s ? "#EEEDFE" : "transparent", color: taskFilter.source === s ? "#3C3489" : "#888", cursor: "pointer", fontFamily: "var(--font)" }}>{s === "all" ? "All" : s === "call_log" ? "From call log" : s === "manual" ? "Manual" : "AI agent"}</button>
              ))}
            </div>
            {(Object.values(taskFilter).some(v => v !== "all") || taskStatusFilter !== "all" || taskSearch) && <button onClick={() => { setTaskFilter({ priority: "all", due: "all", type: "all", source: "all" }); setTaskStatusFilter("all"); setTaskSearch(""); }} style={{ marginLeft: "auto", background: "none", border: "none", color: "#888", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)" }}>Clear all</button>}
          </div>

          {/* Task List — Iter 263 (Jul 2026): virtualized via react-window.
              The pending-task volume (2,151 tenant-wide) was rendering
              ~18,000 live DOM nodes and causing scroll lag + typing jank.
              We now only render ~15 rows in the viewport, with expanded
              rows getting extra height via the rowHeight callback. */}
          {filteredTasks.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-tertiary)", fontSize: 13 }}>
              {tasks.length === 0
                ? "No tasks yet. Create your first task or they'll appear from call logs and AI agents."
                : "No tasks match current filters."}
            </div>
          ) : (
            <VirtualList
              rowCount={filteredTasks.length}
              rowHeight={(index) => filteredTasks[index]?.id === expandedTaskAction ? 260 : 82}
              overscanCount={4}
              defaultHeight={600}
              style={{ height: "calc(100vh - 480px)", minHeight: 400 }}
              rowComponent={TaskRow}
              rowProps={{
                tasks: filteredTasks,
                expandedTaskAction,
                setExpandedTaskAction,
                taskActionNote,
                setTaskActionNote,
                toggleTaskComplete,
                openEditTask,
                deleteTask,
                navigate,
                token,
              }}
            />
          )}
        </div>
        );
      })()}

      {/* Three-panel layout */}
      {commTab === "conversations" && (
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr 300px", flex: 1, overflow: "hidden", borderTop: "0.5px solid var(--border)" }}>
        {/* LEFT PANEL — Conversation List */}
        <div style={{ borderRight: "0.5px solid var(--border)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "0.5px solid var(--border)" }}>
            <span style={{ fontSize: 13, fontWeight: 650 }}>Conversations</span>
            <button className="btn btn-primary" style={{ fontSize: 10, padding: "4px 10px" }} onClick={() => openCompose("email")} data-testid="compose-btn">
              <Plus style={{ width: 10, height: 10 }} /> Compose
            </button>
          </div>
          {/* Filter tabs */}
          <div style={{ display: "flex", borderBottom: "0.5px solid var(--border)", padding: "0 4px" }}>
            {[
              { id: "all", label: "All" },
              { id: "unread", label: "Unread" },
              { id: "needs_action", label: "Needs action" },
              { id: "awaiting", label: "Awaiting" },
            ].map(t => (
              <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`inbox-filter-${t.id}`}
                style={{ flex: 1, padding: "6px 0", fontSize: 10, fontWeight: filter === t.id ? 600 : 400, color: filter === t.id ? "var(--blue)" : "var(--text-tertiary)", background: "none", border: "none", borderBottom: filter === t.id ? "2px solid var(--blue)" : "2px solid transparent", cursor: "pointer" }}>
                {t.label}
              </button>
            ))}
          </div>
          {/* Search */}
          <div style={{ padding: "6px 8px", borderBottom: "0.5px solid var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "var(--gray-light)", borderRadius: 6 }}>
              <Search style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." style={{ border: "none", outline: "none", background: "transparent", fontSize: 11, flex: 1 }} data-testid="comms-search" />
            </div>
            {/* Iter 324 · "Hide internal emails" toggle. Filters out
                threads where every participant address ends with the
                caller's own email domain (typically @ehiehr.com), so
                internal cc noise doesn't clutter the sales inbox. */}
            <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 10, color: "var(--text-secondary)", cursor: "pointer", userSelect: "none" }}
                   data-testid="comms-hide-internal-label">
              <input type="checkbox" checked={hideInternal} onChange={e => setHideInternal(e.target.checked)}
                     data-testid="comms-hide-internal-toggle"
                     style={{ margin: 0, cursor: "pointer" }} />
              Hide internal emails{hideInternal && internalHidden > 0 ? ` (${internalHidden} hidden)` : ""}
            </label>
          </div>
          {/* Conversation rows */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {filtered.length === 0 && (
              <div data-testid="conversations-empty" style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-tertiary)", fontSize: 12 }}>
                {convsLoading && conversations.length === 0
                  ? "Loading your email conversations…"
                  : conversations.length === 0
                    ? "No email conversations yet. Once you send or receive email through your connected Microsoft 365 account, threads will appear here."
                    : "No conversations match current filters."}
              </div>
            )}
            {filtered.map(c => (
              <div key={c.id} onClick={() => setActiveConv(c)} data-testid={`conv-${c.id}`}
                style={{ display: "flex", gap: 8, padding: "10px 12px", cursor: "pointer", borderBottom: "0.5px solid var(--border)", background: activeConv?.id === c.id ? "var(--blue-light)" : "transparent", borderLeft: activeConv?.id === c.id ? "3px solid var(--blue)" : "3px solid transparent", transition: "background 0.1s" }}
                onMouseOver={e => { if (activeConv?.id !== c.id) e.currentTarget.style.background = "#fafaf9"; }}
                onMouseOut={e => { if (activeConv?.id !== c.id) e.currentTarget.style.background = "transparent"; }}>
                {/* Unread dot */}
                <div style={{ width: 8, display: "flex", alignItems: "flex-start", paddingTop: 12, flexShrink: 0 }}>
                  {c.unread && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--blue)" }} />}
                </div>
                {/* Avatar */}
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: c.color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 650, flexShrink: 0 }}>{c.avatar}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Iter 325 (Feb 2026) — Row layout rebuild:
                      Line 1: last-message SUBJECT (bold) + relative time
                      Line 2: contactName · company (company suppressed if
                              identical to contactName so we don't repeat)
                      Line 3: last message preview
                      Line 4: channel pill + optional signals

                      Timestamps are formatted relatively ("2h ago",
                      "yesterday", "Jul 27") so the raw ISO no longer
                      leaks through the UI. */}
                  {(() => {
                    const lastMsg = c.messages && c.messages[c.messages.length - 1];
                    const subj = (lastMsg && lastMsg.subject) || c.lastMessage || "(no subject)";
                    const rel = (() => {
                      if (!c.lastTime) return "";
                      const t = new Date(c.lastTime);
                      if (Number.isNaN(t.getTime())) return String(c.lastTime).split("T")[0];
                      const now = new Date();
                      const diffMin = Math.round((now - t) / 60000);
                      if (diffMin < 1) return "just now";
                      if (diffMin < 60) return `${diffMin}m ago`;
                      const diffHr = Math.round(diffMin / 60);
                      if (diffHr < 24) return `${diffHr}h ago`;
                      const diffDay = Math.round(diffHr / 24);
                      if (diffDay === 1) return "yesterday";
                      if (diffDay < 7) return `${diffDay}d ago`;
                      return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                    })();
                    const sameNameCo = (c.company || "").trim().toLowerCase() === (c.contactName || "").trim().toLowerCase();
                    return (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: c.unread ? 700 : 600, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }} title={subj}>{subj}</span>
                          <span style={{ fontSize: 9, color: "var(--text-tertiary)", whiteSpace: "nowrap" }}>{rel}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "var(--text-secondary)", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          <span style={{ fontWeight: 550 }}>{c.contactName}</span>
                          {!sameNameCo && c.company ? <span style={{ color: "var(--text-tertiary)" }}> · {c.company}</span> : null}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-tertiary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.lastMessage || (lastMsg && lastMsg.preview) || ""}</div>
                      </>
                    );
                  })()}
                  <div style={{ display: "flex", gap: 3, marginTop: 3, alignItems: "center" }}>
                    <span className={`tag ${c.messages[c.messages.length-1]?.channel === "sms" ? "tag-green" : "tag-blue"}`} style={{ fontSize: 9 }}>{c.messages[c.messages.length-1]?.channel === "sms" ? "SMS" : "Email"}</span>
                    {c.aiSignal && <SignalPill text={c.aiSignal.text} color={c.aiSignal.color} />}
                    {c.needsAction && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--amber)", marginLeft: 2 }} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CENTER PANEL — Active Conversation */}
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {!activeConv ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)" }}>
              <MessageSquare style={{ width: 32, height: 32, marginBottom: 8 }} />
              <div style={{ fontSize: 13 }}>Select a conversation to view</div>
              <div style={{ fontSize: 11, marginBottom: 12 }}>or compose a new message</div>
              <button className="btn" onClick={() => openCompose("email")}>Compose new message</button>
            </div>
          ) : (
            <>
              {/* Conv header */}
              <div style={{ padding: "10px 16px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: activeConv.color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 650 }}>{activeConv.avatar}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 650 }}>{activeConv.contactName}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                    {activeConv.company} <span className="tag tag-blue" style={{ fontSize: 9, marginLeft: 4 }}>{(activeConv.stage || "").replace(/_/g," ")}</span> <span className="tag tag-gray" style={{ fontSize: 9 }}>{activeConv.subStatus}</span>
                  </div>
                </div>
                <button className="btn" style={{ padding: "5px 8px" }} onClick={() => openCompose("call")}><Phone style={{ width: 12, height: 12 }} /></button>
                <button className="btn" style={{ padding: "5px 8px" }} onClick={() => openCompose("email")}><Mail style={{ width: 12, height: 12 }} /></button>
                <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => openCompose("email")} data-testid="conv-compose-btn"><Plus style={{ width: 11, height: 11 }} /> Compose</button>
              </div>
              {/* Analysis bar */}
              {analysisBar}
              {/* Message thread */}
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
                {activeConv.messages.map((m, i) => {
                  const isOut = m.direction === "outbound";
                  const prevMsg = activeConv.messages[i - 1];
                  const showDate = !prevMsg || new Date(m.time).toDateString() !== new Date(prevMsg.time).toDateString();
                  return (
                    <div key={m.id}>
                      {showDate && (
                        <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
                          <span style={{ fontSize: 10, color: "var(--text-tertiary)", background: "var(--gray-light)", padding: "2px 10px", borderRadius: 10 }}>
                            {new Date(m.time).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                          </span>
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: isOut ? "flex-end" : "flex-start", marginBottom: 10 }}>
                        <div style={{ maxWidth: "70%" }}>
                          {m.subject && <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginBottom: 2, textAlign: isOut ? "right" : "left" }}>Subject: {m.subject}</div>}
                          <div style={{
                            padding: "10px 14px", fontSize: 13, lineHeight: 1.5,
                            background: isOut ? "#0071e3" : "var(--surface)",
                            color: isOut ? "#fff" : "var(--text-primary)",
                            border: isOut ? "none" : "0.5px solid var(--border)",
                            borderRadius: isOut ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                          }}>
                            {m.content}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3, justifyContent: isOut ? "flex-end" : "flex-start" }}>
                            <span style={{ fontSize: 9, color: "var(--text-tertiary)" }}>
                              {m.channel === "email" ? "Email · Microsoft 365" : "SMS · RingCentral"} · {m.status}
                            </span>
                            <span style={{ fontSize: 9, color: "var(--text-tertiary)" }}>
                              {new Date(m.time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                            </span>
                          </div>
                          {/* Signal pills */}
                          {m.signals && m.signals.length > 0 && (
                            <div style={{ display: "flex", gap: 3, marginTop: 3, justifyContent: isOut ? "flex-end" : "flex-start" }}>
                              {m.signals.map((s, j) => <SignalPill key={j} text={s} color={s.toLowerCase().includes("ready") || s.toLowerCase().includes("proceed") ? "var(--green)" : s.toLowerCase().includes("reschedule") ? "var(--red)" : "var(--blue)"} />)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={threadEndRef} />
              </div>
              {/* Compose bar */}
              <div style={{ borderTop: "0.5px solid var(--border)", padding: "10px 16px", flexShrink: 0 }}>
                <div style={{ display: "flex", gap: 2, marginBottom: 6 }}>
                  {[
                    { id: "email", label: "Email", icon: Mail },
                    { id: "sms", label: "SMS", icon: MessageSquare },
                    { id: "call", label: "Call", icon: Phone },
                    { id: "note", label: "Note", icon: FileText },
                  ].map(t => (
                    <button key={t.id} onClick={() => openCompose(t.id)}
                      style={{ display: "flex", alignItems: "center", gap: 3, padding: "4px 10px", fontSize: 10, background: "none", border: "0.5px solid var(--border)", borderRadius: 4, cursor: "pointer", color: "var(--text-secondary)" }}>
                      <t.icon style={{ width: 10, height: 10 }} /> {t.label}
                    </button>
                  ))}
                  <div style={{ flex: 1 }} />
                  <button className="btn" style={{ fontSize: 10, padding: "4px 10px", color: "var(--blue)", borderColor: "var(--blue)" }} onClick={() => openCompose("email")}>
                    <Sparkles style={{ width: 10, height: 10 }} /> Draft with Claude
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT PANEL — Lead Context + Dialer */}
        <div style={{ borderLeft: "0.5px solid var(--border)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Lead Context */}
          <div style={{ flex: 1, overflowY: "auto", padding: activeConv ? "12px" : "0" }}>
            {!activeConv ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--text-tertiary)", fontSize: 11 }}>Select a conversation to see lead context</div>
            ) : (
              <>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 650 }}>{activeConv.company}</div>
                  <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{activeConv.contactName}</div>
                </div>
                <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                  <span className="tag tag-blue">{(activeConv.stage || "").replace(/_/g," ")}</span>
                  <span className="tag tag-purple">AI {activeConv.aiScore}</span>
                </div>
                {/* Next action */}
                {activeConv.nextAction && (
                  <div style={{ padding: "8px 10px", background: "#f0f5fd", borderRadius: 8, marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 3 }}>Next action (Claude)</div>
                    <div style={{ fontSize: 12, color: "var(--blue)", fontWeight: 500, lineHeight: 1.4 }}>{activeConv.nextAction}</div>
                    <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                      <button className="btn" style={{ fontSize: 9, padding: "2px 8px" }} onClick={() => toast.success("Marked done")}>Mark done</button>
                      <button className="btn" style={{ fontSize: 9, padding: "2px 8px" }}>Edit</button>
                      <button className="btn" style={{ fontSize: 9, padding: "2px 8px" }}>Snooze 2 days</button>
                    </div>
                  </div>
                )}
                {/* Quick stats */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4 }}>Quick stats</div>
                  {[
                    ["Last contact", activeConv.lastTime],
                    ["Deal value", `$${(activeConv.value || 0).toLocaleString()}`],
                    ["AI Score", activeConv.aiScore],
                  ].map(([l, v], i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 11 }}>
                      <span style={{ color: "var(--text-tertiary)" }}>{l}</span>
                      <span style={{ fontWeight: 560 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <button className="btn" style={{ width: "100%", fontSize: 11 }} onClick={() => toast.info("Opening lead panel")}>
                  <ExternalLink style={{ width: 11, height: 11 }} /> Open lead
                </button>
              </>
            )}
          </div>
          {/* RingCentral Dialer — auto-prefills from active conversation OR
             from `?phone=` URL param (used by Lead Focus → click-to-call). */}
          <DialerPanel prefillNumber={activeConv?.phone || searchParams.get("phone") || ""} />
        </div>
      </div>
      )}

      {/* Compose Modal */}
      <ComposeModal open={composeOpen} onClose={() => { setComposeOpen(false); setQuoteContext(null); }} lead={quoteContext ? {
        id: quoteContext.quote?.lead_id, contact_name: quoteContext.contact?.name || quoteContext.quote?.contact_name,
        company: quoteContext.contact?.company || quoteContext.quote?.contact_company,
        email: quoteContext.contact?.email || quoteContext.quote?.contact_email,
        phone: quoteContext.contact?.phone, stage: "Proposal",
      } : activeConv ? {
        id: activeConv.leadId, contact_name: activeConv.contactName, company: activeConv.company,
        email: activeConv.email, phone: activeConv.phone, stage: activeConv.stage,
        sub_status: activeConv.subStatus, next_action: activeConv.nextAction,
      } : null} mode={composeMode} onSend={() => { toast.success("Communication logged"); fetchConversations(); }} quoteContext={quoteContext} />

      {/* FollowUpTaskModal — opens when ?lead_id=… is in the URL */}
      {showFollowUp && followUpLead && (
        <FollowUpTaskModal
          lead={followUpLead}
          onClose={() => setShowFollowUp(false)}
          onSave={async (payload) => {
            try {
              await axios.post(`${API}/tasks`, {
                title: payload.title,
                description: payload.notes || "",
                priority: (payload.priority || "Medium").toLowerCase(),
                category: "follow_up",
                due_date: payload.due || null,
                assigned_to_name: payload.assignee === "Me" ? (user?.full_name || user?.email || "Me") : payload.assignee,
                lead_name: followUpLead.clinic || followUpLead.contact_name || urlLeadName || "",
                lead_id: followUpLead.id || urlLeadId || undefined,
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success("Task created");
              fetchTasks();
            } catch { toast.error("Failed to create task"); }
          }}
        />
      )}

      {/* Create Task Modal (2B — shared component used by Lead Focus too) */}
      <CreateTaskModal
        open={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        onCreated={() => fetchTasks()}
      />

      {/* Iter 159 (#48003f98 / Ioannis) — Edit Task Modal */}
      {editTaskState && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setEditTaskState(null)}>
          <div style={{ background: "#fff", borderRadius: 12, width: 480, border: "0.5px solid #eee" }} onClick={e => e.stopPropagation()} data-testid="comm-edit-task-modal">
            <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Edit task</span>
              <button onClick={() => setEditTaskState(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 12 }}><label style={{ fontSize: 11, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Title *</label><input className="input" value={editTaskForm.title} onChange={e => setEditTaskForm(f => ({ ...f, title: e.target.value }))} data-testid="comm-edit-task-title" /></div>
              <div style={{ marginBottom: 12 }}><label style={{ fontSize: 11, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Description / Notes</label><textarea className="input" rows={3} value={editTaskForm.description} onChange={e => setEditTaskForm(f => ({ ...f, description: e.target.value }))} style={{ resize: "vertical" }} data-testid="comm-edit-task-description" /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div><label style={{ fontSize: 11, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Priority</label>
                  <select className="input" value={editTaskForm.priority} onChange={e => setEditTaskForm(f => ({ ...f, priority: e.target.value }))} data-testid="comm-edit-task-priority">
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                  </select></div>
                <div><label style={{ fontSize: 11, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Due date</label>
                  <input type="date" className="input" value={editTaskForm.due_date} onChange={e => { const v = e.target.value; setEditTaskForm(f => ({ ...f, due_date: v, due_time: v ? f.due_time : "" })); }} data-testid="comm-edit-task-due-date" /></div>
                <div><label style={{ fontSize: 11, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>Time</label>
                  <TaskTimeInput date={editTaskForm.due_date} value={editTaskForm.due_time} onChange={v => setEditTaskForm(f => ({ ...f, due_time: v }))} testid="comm-edit-task-due-time" /></div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button onClick={() => setEditTaskState(null)} className="btn" style={{ fontSize: 12 }}>Cancel</button>
                <button onClick={saveEditTask} className="btn btn-primary" style={{ fontSize: 12 }} data-testid="comm-save-edit-task-btn">Save changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
